import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Plus, Edit, Trash2, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PackagesPage = () => {
  const [packages, setPackages] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, pkg: null });
  const [form, setForm] = useState({
    name: '',
    description: '',
    price: '',
    event_type: '',
    items: []
  });

  useEffect(() => {
    fetchPackages();
    fetchProducts();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data);
    } catch (error) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to load products');
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      price: '',
      event_type: '',
      items: []
    });
    setEditingPackage(null);
  };

  const openEditDialog = (pkg) => {
    setEditingPackage(pkg);
    setForm({
      name: pkg.name,
      description: pkg.description || '',
      price: pkg.price.toString(),
      event_type: pkg.event_type || '',
      items: pkg.items.map(item => ({ 
        product_id: item.product_id, 
        quantity: item.quantity,
        selected_variations: item.selected_variations || {}
      }))
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.price || form.items.length === 0) {
      toast.error('Please fill in required fields and add at least one item');
      return;
    }

    try {
      const data = {
        name: form.name,
        description: form.description || null,
        price: parseFloat(form.price),
        cost: null,  // Cost is now auto-calculated from products
        event_type: form.event_type || null,
        items: form.items
      };

      if (editingPackage) {
        await axios.put(`${API}/packages/${editingPackage.id}`, data);
        toast.success('Package updated');
      } else {
        await axios.post(`${API}/packages`, data);
        toast.success('Package created');
      }

      setShowDialog(false);
      resetForm();
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save package');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.pkg) return;
    try {
      await axios.delete(`${API}/packages/${deleteDialog.pkg.id}`);
      toast.success('Package deleted');
      setDeleteDialog({ open: false, pkg: null });
      fetchPackages();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  const toggleProduct = (productId) => {
    setForm(prev => {
      const exists = prev.items.find(item => item.product_id === productId);
      if (exists) {
        return { ...prev, items: prev.items.filter(item => item.product_id !== productId) };
      }
      return { ...prev, items: [...prev.items, { product_id: productId, quantity: 1, selected_variations: {} }] };
    });
  };

  const updateQuantity = (productId, quantity) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.product_id === productId ? { ...item, quantity: parseInt(quantity) || 1 } : item
      )
    }));
  };

  const updateItemVariation = (productId, variationName, optionName) => {
    setForm(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.product_id === productId 
          ? { ...item, selected_variations: { ...item.selected_variations, [variationName]: optionName } }
          : item
      )
    }));
  };

  const getProductName = (productId) => {
    return products.find(p => p.id === productId)?.name || 'Unknown Product';
  };

  const calculateRetailTotal = () => {
    let total = 0;
    for (const item of form.items) {
      const product = products.find(p => p.id === item.product_id);
      if (product) {
        let itemPrice = product.price;
        // Add variation price adjustments
        if (item.selected_variations && product.variations) {
          for (const variation of product.variations) {
            const selectedOptionName = item.selected_variations[variation.name];
            if (selectedOptionName) {
              const option = variation.options?.find(o => o.name === selectedOptionName);
              if (option) {
                itemPrice += option.price_adjustment || 0;
              }
            }
          }
        }
        total += itemPrice * (item.quantity || 1);
      }
    }
    return total;
  };

  const calculateCostTotal = () => {
    let total = 0;
    for (const item of form.items) {
      const product = products.find(p => p.id === item.product_id);
      if (product) {
        let itemCost = product.cost || 0;
        // Add variation costs
        if (item.selected_variations && product.variations) {
          for (const variation of product.variations) {
            const selectedOptionName = item.selected_variations[variation.name];
            if (selectedOptionName) {
              const option = variation.options?.find(o => o.name === selectedOptionName);
              if (option && option.cost) {
                itemCost += option.cost;
              }
            }
          }
        }
        total += itemCost * (item.quantity || 1);
      }
    }
    return total;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Packages</h1>
          <p className="text-slate-600 mt-1">Create pre-defined product bundles for your clients</p>
        </div>
        <Button 
          className="bg-blue-600 hover:bg-blue-700" 
          onClick={() => { resetForm(); setShowDialog(true); }}
          data-testid="new-package-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Package
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-64 bg-stone-200 rounded-sm animate-pulse" />
          ))}
        </div>
      ) : packages.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <Card key={pkg.id} className="border-stone-200 card-hover">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg text-stone-800">{pkg.name}</CardTitle>
                    {pkg.event_type && (
                      <Badge className="mt-2 bg-amber-100 text-amber-700 capitalize">
                        {pkg.event_type}
                      </Badge>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-stone-800 price-display">
                      {formatCurrency(pkg.price)}
                    </span>
                    {pkg.cost && (
                      <p className="text-xs text-green-600">
                        Profit: {formatCurrency(pkg.price - pkg.cost)}
                      </p>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {pkg.description && (
                  <p className="text-sm text-stone-600 mb-4">{pkg.description}</p>
                )}
                {pkg.cost && (
                  <div className="mb-3 p-2 bg-stone-50 rounded text-xs">
                    <span className="text-stone-500">Cost: {formatCurrency(pkg.cost)}</span>
                  </div>
                )}
                <div className="space-y-2 mb-4">
                  <p className="text-xs font-medium text-stone-500 uppercase">Includes:</p>
                  {pkg.items.map((item, index) => {
                    const product = products.find(p => p.id === item.product_id);
                    const hasVariations = item.selected_variations && Object.keys(item.selected_variations).length > 0;
                    return (
                      <div key={index} className="text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-stone-700">{getProductName(item.product_id)}</span>
                          <Badge variant="outline" className="text-xs">×{item.quantity}</Badge>
                        </div>
                        {hasVariations && (
                          <div className="pl-3 text-xs text-slate-500">
                            {Object.entries(item.selected_variations).map(([varName, optionName]) => (
                              <span key={varName} className="mr-2">
                                {varName}: <span className="text-slate-700">{optionName}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => openEditDialog(pkg)}
                    data-testid={`edit-package-${pkg.id}`}
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setDeleteDialog({ open: true, pkg })}
                    data-testid={`delete-package-${pkg.id}`}
                  >
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">No packages yet</h3>
            <p className="text-slate-500 mb-6">
              {products.length === 0 
                ? 'Create products first, then bundle them into packages'
                : 'Create your first package to offer product bundles'}
            </p>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => { resetForm(); setShowDialog(true); }}
              disabled={products.length === 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Package
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Package Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPackage ? 'Edit Package' : 'New Package'}</DialogTitle>
            <DialogDescription>
              {editingPackage ? 'Update package details' : 'Create a bundle of products'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Package Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Sports Package A"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="package-name-input"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Price (CAD) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  data-testid="package-price-input"
                  required
                />
              </div>
            </div>

            {/* Pricing Comparison */}
            {form.items.length > 0 && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-amber-800">If sold separately:</span>
                  <span className="font-semibold text-amber-900">{formatCurrency(calculateRetailTotal())}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-amber-800">Package price:</span>
                  <span className="font-semibold text-amber-900">{formatCurrency(parseFloat(form.price) || 0)}</span>
                </div>
                <div className="flex justify-between text-sm border-t border-amber-200 pt-1">
                  <span className="text-amber-800">Customer saves:</span>
                  <span className="font-bold text-green-700">
                    {formatCurrency(Math.max(0, calculateRetailTotal() - (parseFloat(form.price) || 0)))}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-600 border-t border-amber-200 pt-1">
                  <span>Total cost of goods:</span>
                  <span>{formatCurrency(calculateCostTotal())}</span>
                </div>
              </div>
            )}

            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>Tip:</strong> Set variations for each product to lock in specific options (like ornament shapes).
                Cost of goods is calculated from products and their selected variations.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Event Type</Label>
              <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                <SelectTrigger data-testid="package-event-type-select">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sports">Sports</SelectItem>
                  <SelectItem value="school">School</SelectItem>
                  <SelectItem value="portrait">Portrait</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional package description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="package-description-input"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Products in Package *</Label>
              {products.length === 0 ? (
                <p className="text-sm text-slate-500">No products available. Create products first.</p>
              ) : (
                <div className="border border-slate-200 rounded-lg divide-y max-h-64 overflow-y-auto">
                  {products.map(product => {
                    const isSelected = form.items.some(item => item.product_id === product.id);
                    const item = form.items.find(item => item.product_id === product.id);
                    return (
                      <div key={product.id} className="p-3 space-y-2">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            id={`product-${product.id}`}
                            checked={isSelected}
                            onCheckedChange={() => toggleProduct(product.id)}
                            data-testid={`select-product-${product.id}`}
                          />
                          <label 
                            htmlFor={`product-${product.id}`}
                            className="flex-1 text-sm cursor-pointer"
                          >
                            {product.name}
                            <span className="text-slate-500 ml-2">{formatCurrency(product.price)}</span>
                          </label>
                          {isSelected && (
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-slate-500">Qty:</span>
                              <Input
                                type="number"
                                min="1"
                                className="w-14 h-7 text-sm"
                                value={item?.quantity || 1}
                                onChange={(e) => updateQuantity(product.id, e.target.value)}
                                data-testid={`quantity-${product.id}`}
                              />
                            </div>
                          )}
                        </div>
                        {/* Variation selectors when product is selected */}
                        {isSelected && product.variations && product.variations.length > 0 && (
                          <div className="pl-7 grid grid-cols-2 gap-2">
                            {product.variations.map(variation => (
                              <div key={variation.name} className="space-y-1">
                                <Label className="text-xs text-slate-600">{variation.name}</Label>
                                <Select
                                  value={item?.selected_variations?.[variation.name] || ''}
                                  onValueChange={(val) => updateItemVariation(product.id, variation.name, val)}
                                >
                                  <SelectTrigger className="h-7 text-xs">
                                    <SelectValue placeholder={`Select ${variation.name}`} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {variation.options?.map(option => (
                                      <SelectItem key={option.name} value={option.name}>
                                        {option.name}
                                        {option.price_adjustment !== 0 && (
                                          <span className="text-slate-500 ml-1">
                                            ({option.price_adjustment > 0 ? '+' : ''}{formatCurrency(option.price_adjustment)})
                                          </span>
                                        )}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-600 hover:bg-blue-700" 
                data-testid="save-package-btn"
                disabled={products.length === 0}
              >
                {editingPackage ? 'Update' : 'Create'} Package
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, pkg: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Package</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteDialog.pkg?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, pkg: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-package-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesPage;
